<script setup lang="ts">
/**
 * ConfirmHost — глобальный рендер CDP-модала подтверждения (см. useConfirm).
 * Монтируется один раз в App.vue рядом с ToastHost. Рисует верхний запрос из
 * очереди, поддерживает Enter (подтвердить) / Esc (отмена), клик по бэкдропу =
 * отмена. Тема — через surface-переменные, работает в светлой и тёмной.
 */
import { computed, watch, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";
import { AlertTriangle, HelpCircle, X } from "lucide-vue-next";
import { useConfirm } from "../../composables/useConfirm";

const { t } = useI18n();
const { state, resolveTop } = useConfirm();

const current = computed(() => state.queue[0] ?? null);
const confirmBtn = ref<HTMLButtonElement | null>(null);

const title = computed(() => current.value?.title ?? t("common.confirm_title"));
const confirmLabel = computed(
  () => current.value?.confirmLabel ?? (current.value?.tone === "danger" ? t("common.delete") : t("common.confirm")),
);
const cancelLabel = computed(() => current.value?.cancelLabel ?? t("common.cancel"));
const isDanger = computed(() => current.value?.tone === "danger");

function decide(ok: boolean): void {
  if (!current.value) return;
  resolveTop(ok);
}

function onKey(e: KeyboardEvent): void {
  if (!current.value) return;
  if (e.key === "Escape") {
    e.preventDefault();
    decide(false);
  } else if (e.key === "Enter") {
    e.preventDefault();
    decide(true);
  }
}

// Автофокус на кнопке подтверждения при появлении диалога.
watch(current, async (c) => {
  if (c) {
    await nextTick();
    confirmBtn.value?.focus();
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="current"
      class="pointer-events-auto fixed inset-0 z-[1100] bg-surface-900/50 flex items-start justify-center pt-24 p-4"
      data-testid="confirm-host"
      tabindex="-1"
      @click.self="decide(false)"
      @keydown="onKey"
    >
      <div class="bg-surface-0 rounded-lg shadow-xl w-full max-w-md border border-surface-200" role="alertdialog" aria-modal="true">
        <header class="flex items-start gap-3 px-5 pt-5">
          <div
            class="shrink-0 size-9 rounded-full flex items-center justify-center"
            :class="isDanger ? 'bg-danger-500/10 text-danger-600' : 'bg-accent-500/10 text-accent-600'"
          >
            <AlertTriangle v-if="isDanger" class="size-5" />
            <HelpCircle v-else class="size-5" />
          </div>
          <div class="flex-1 min-w-0 pt-1">
            <h2 class="font-semibold text-surface-900">{{ title }}</h2>
          </div>
          <button
            class="text-surface-400 hover:text-surface-700 shrink-0"
            :aria-label="t('common.close')"
            @click="decide(false)"
          >
            <X class="size-5" />
          </button>
        </header>

        <div class="px-5 py-4 pl-[4.25rem] text-sm text-surface-700 whitespace-pre-line">
          {{ current.message }}
        </div>

        <footer class="flex items-center justify-end gap-2 px-5 py-3 border-t border-surface-200 bg-surface-50 rounded-b-lg">
          <button class="btn-ghost text-sm" data-testid="confirm-cancel" @click="decide(false)">
            {{ cancelLabel }}
          </button>
          <button
            ref="confirmBtn"
            class="text-sm"
            :class="isDanger ? 'btn-danger' : 'btn-primary'"
            data-testid="confirm-ok"
            @click="decide(true)"
          >
            {{ confirmLabel }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>
